import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { MaskedLines } from "@/lib/motion";
import { useEnquiry } from "@/context/EnquiryContext";
import { scrollToId } from "@/lib/scroll";
import { IMAGES } from "@/data/content";

export default function Hero() {
  const ref = useRef(null);
  const { openEnquiry } = useEnquiry();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const overlay = useTransform(scrollYProgress, [0, 1], [0.35, 0.7]);

  const scrollTo = (id) => scrollToId(id);

  return (
    <section ref={ref} data-testid="hero" className="relative min-h-screen w-full overflow-hidden bg-brand-ink">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={IMAGES.hero} alt="Tractor with agricultural implements working in a farm field" className="size-full object-cover" />
      </motion.div>
      <motion.div style={{ opacity: overlay }} className="absolute inset-0 bg-brand-ink" />
      <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/90 via-brand-ink/20 to-brand-ink/40" />
      <div className="grain" />

      <div className="relative z-10 mx-auto max-w-7xl px-5 lg:px-8 min-h-screen flex flex-col justify-end pb-16 pt-32">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="mb-6 inline-flex items-center gap-2 self-start rounded-full border border-brand-cream/25 bg-brand-cream/10 backdrop-blur-md px-4 py-1.5"
        >
          <span className="size-1.5 rounded-full bg-brand-greenLight animate-pulse" />
          <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-brand-cream/90">Powering Modern Agriculture · Premium Farm Implements</span>
        </motion.div>

        <h1 className="font-heading font-extrabold text-brand-cream tracking-tighter leading-[0.95] text-4xl sm:text-6xl lg:text-[5.5rem] max-w-5xl">
          <MaskedLines
            lines={["Empowering Farmers", "with Modern", "Agricultural Machinery"]}
            lineClassName=""
          />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-7 max-w-2xl text-base sm:text-lg leading-relaxed text-brand-cream/80"
        >
          ADK SOLUTIONS provides premium agricultural machinery designed to improve productivity, save water, reduce labor costs, and support sustainable farming.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-9 flex flex-wrap items-center gap-3"
        >
          <Button data-testid="hero-explore" onClick={() => scrollTo("products")} className="rounded-full bg-brand-green text-white hover:bg-brand-greenLight h-12 px-7 text-sm">
            Explore Machinery <ArrowUpRight className="size-4" />
          </Button>
          <Button data-testid="hero-quote" onClick={() => openEnquiry("", "enquiry")} variant="outline" className="rounded-full border-brand-cream/40 bg-brand-cream/5 text-brand-cream hover:bg-brand-cream hover:text-brand-ink h-12 px-7 text-sm backdrop-blur-md">
            Request a Quote
          </Button>
          <Button data-testid="hero-contact" onClick={() => scrollTo("contact")} variant="ghost" className="rounded-full text-brand-cream hover:bg-brand-cream/10 hover:text-white h-12 px-6 text-sm">
            Contact Us
          </Button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4 }}
          className="mt-14 grid grid-cols-3 gap-6 max-w-lg border-t border-brand-cream/15 pt-6"
        >
          {[
            { k: "30%", v: "Water saved" },
            { k: "5", v: "Premium implements" },
            { k: "100%", v: "Genuine products" },
          ].map((s) => (
            <div key={s.v}>
              <div className="font-heading font-bold text-2xl sm:text-3xl text-brand-cream">{s.k}</div>
              <div className="text-xs text-brand-cream/60 mt-1">{s.v}</div>
            </div>
          ))}
        </motion.div>
      </div>

      <motion.button
        onClick={() => scrollTo("about")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        aria-label="Scroll down"
        className="absolute bottom-6 right-6 z-10 hidden sm:grid place-items-center size-12 rounded-full border border-brand-cream/30 text-brand-cream/80 hover:bg-brand-cream hover:text-brand-ink transition-colors"
      >
        <ArrowDown className="size-5 animate-bounce" />
      </motion.button>
    </section>
  );
}
